import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './NtripClientConfigEditor.module.scss';

const DEFAULT_HOST = 'nrtk-swepos.lm.se';
const DEFAULT_PORT = '8500';
const DEFAULT_MOUNT_POINT = 'RTCM3_GNSS';

function NtripClientConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [mountPoint, setMountPoint] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const lastConfigDataRef = useRef('');
  const isUpdatingFromGuiRef = useRef(false);

  // Generate JSON from GUI settings
  const generateConfig = useCallback(() => {
    const config = {
      host,
      port: parseInt(port, 10) || 0,
      mount_point: mountPoint,
      username,
      password,
    };

    return JSON.stringify(config, null, 2);
  }, [host, port, mountPoint, username, password]);

  // Parse JSON to GUI when configData changes from external source
  useEffect(() => {
    if (
      configData &&
      configData !== lastConfigDataRef.current &&
      !isUpdatingFromGuiRef.current &&
      !isEditingJson
    ) {
      lastConfigDataRef.current = configData;

      try {
        const config = JSON.parse(configData);

        setHost(config.host !== undefined ? String(config.host) : '');
        setPort(config.port !== undefined ? String(config.port) : '');
        setMountPoint(config.mount_point !== undefined ? String(config.mount_point) : '');
        setUsername(config.username !== undefined ? String(config.username) : '');
        setPassword(config.password !== undefined ? String(config.password) : '');
      } catch (error) {
        console.error('Error parsing ntrip client config:', error);
      }
    }
  }, [configData, isEditingJson]);

  // Update parent component when GUI settings change
  useEffect(() => {
    // Skip the first render and when we're parsing from external config
    if (lastConfigDataRef.current) {
      isUpdatingFromGuiRef.current = true;
      const newConfig = generateConfig();
      lastConfigDataRef.current = newConfig;
      onConfigChange(newConfig);

      // Reset the flag after a short delay to allow the parent to update
      setTimeout(() => {
        isUpdatingFromGuiRef.current = false;
      }, 10);
    }
  }, [host, port, mountPoint, username, password, generateConfig, onConfigChange]);

  const handlePortChange = value => {
    if (value === '' || /^\d*$/.test(value)) {
      setPort(value);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>NTRIP Client Settings</h3>

      <div className={`${styles.setting} ${styles.settingFull}`}>
        <div className={`${styles.fieldGrid} ${styles.ntripFields}`}>
          <div className={styles.field}>
            <label className={styles.inputLabel} htmlFor="ntrip-host">
              Host
            </label>
            <div className={styles.inputRow}>
              <input
                id="ntrip-host"
                type="text"
                value={host}
                onChange={e => setHost(e.target.value)}
                className={styles.input}
                placeholder="nrtk-swepos.lm.se"
              />
              <button
                type="button"
                className={styles.fillButton}
                onClick={() => setHost(DEFAULT_HOST)}
              >
                Fill {DEFAULT_HOST}
              </button>
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.inputLabel} htmlFor="ntrip-port">
              Port
            </label>
            <div className={styles.inputRow}>
              <input
                id="ntrip-port"
                type="text"
                value={port}
                onChange={e => handlePortChange(e.target.value)}
                className={styles.input}
                placeholder="8500"
              />
              <button
                type="button"
                className={styles.fillButton}
                onClick={() => setPort(DEFAULT_PORT)}
              >
                Fill {DEFAULT_PORT}
              </button>
            </div>
            <span className={styles.hint}>Fill 0 to disable NTRIP</span>
          </div>

          <div className={styles.field}>
            <label className={styles.inputLabel} htmlFor="ntrip-mount-point">
              Mount Point
            </label>
            <div className={styles.inputRow}>
              <input
                id="ntrip-mount-point"
                type="text"
                value={mountPoint}
                onChange={e => setMountPoint(e.target.value)}
                className={styles.input}
                placeholder="RTCM3_GNSS"
              />
              <button
                type="button"
                className={styles.fillButton}
                onClick={() => setMountPoint(DEFAULT_MOUNT_POINT)}
              >
                Fill {DEFAULT_MOUNT_POINT}
              </button>
            </div>
          </div>

          <label className={styles.field}>
            <span className={styles.inputLabel}>Username</span>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className={styles.input}
              placeholder="Enter username"
            />
          </label>

          <label className={styles.field}>
            <span className={styles.inputLabel}>Password</span>
            <input
              type="text"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Enter password"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

export default NtripClientConfigEditor;
